import { fastTravelFuelCost, warpFuelCost } from "./fuel";
import type { NpcHullTierLabel } from "./types";

/**
 * What a jump costs, and what it leaves you with (16.104, Don's direction).
 *
 * 16.103 ended by naming the gap: the Distress Signal (16.54) *rescues* a
 * stranded captain, but nothing ever *prevented* the strand. Fuel was visible,
 * the cost was visible on the Engine module, and the one place the two mattered
 * together -- the moment of pressing the button -- said neither.
 *
 * So the button now carries the price and the balance after it. The whole point
 * is the `leaves` figure rather than the cost: a captain who can see
 * "leaves 1 jump" before committing will not accidentally spend their way into a
 * rescue, and one who does it anyway has chosen to.
 *
 * Pure and separate from the view so the arithmetic is testable -- the button it
 * feeds only exists inside a live canvas HUD.
 */

export type TravelKind = "fastTravel" | "warp";

export interface TravelCostPreview {
  kind: TravelKind;
  cost: number;
  fuelAfter: number;
  /** Fast Travels still possible after paying for this one. */
  jumpsAfter: number;
  /** Warps still possible after paying for this one. */
  warpsAfter: number;
  /** Can this jump be paid for at all? */
  affordable: boolean;
  /**
   * True when this jump is affordable but leaves the ship unable to make
   * another -- the one-way trip. Distinct from `!affordable`, because the
   * warning is different: one says "you cannot", the other says "you can, once".
   */
  lastJump: boolean;
}

export function travelCostPreview(
  kind: TravelKind,
  hullTierLabel: NpcHullTierLabel,
  fuelOnBoard: number
): TravelCostPreview {
  const cost = kind === "warp" ? warpFuelCost(hullTierLabel) : fastTravelFuelCost(hullTierLabel);
  const fastTravelCost = fastTravelFuelCost(hullTierLabel);
  const warpCost = warpFuelCost(hullTierLabel);
  const affordable = fuelOnBoard >= cost;
  const fuelAfter = Math.max(0, fuelOnBoard - cost);
  const jumpsAfter = fastTravelCost > 0 ? Math.floor(fuelAfter / fastTravelCost) : 0;
  return {
    kind,
    cost,
    fuelAfter,
    jumpsAfter,
    warpsAfter: warpCost > 0 ? Math.floor(fuelAfter / warpCost) : 0,
    affordable,
    lastJump: affordable && jumpsAfter === 0
  };
}

/**
 * The second line on the button.
 *
 * Reads as a consequence rather than a stat, because that is what the captain is
 * weighing in the half-second before they commit. "Leaves 3 jumps" is a fact
 * they can act on; "260 fuel remaining" is a fact they would have to convert.
 */
export function travelCostLabel(p: TravelCostPreview): string {
  if (!p.affordable) return `needs ${p.cost} fuel`;
  if (p.lastJump) return `${p.cost} fuel — LAST JUMP`;
  return `${p.cost} fuel — leaves ${p.jumpsAfter} jump${p.jumpsAfter === 1 ? "" : "s"}`;
}
